import React from 'react';
import { motion } from 'framer-motion';
import { MapPin, TrendingUp, TrendingDown, Lock, Sparkles, Coffee, Dumbbell, Music, ShoppingBag, Trees, Waves, UtensilsCrossed, Book, Heart } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';

interface VenueAnalysisProps {
  isPremium: boolean;
  onUpgrade: () => void;
  venueScores: any;
}

const VenueAnalysis: React.FC<VenueAnalysisProps> = ({ isPremium, onUpgrade, venueScores }) => {
  const getIcon = (venue: string) => {
    const icons: { [key: string]: any } = {
      gym: Dumbbell,
      nightclub: Music,
      bar: Coffee,
      coffeeshop: Coffee,
      mall: ShoppingBag,
      park: Trees,
      beach: Waves,
      restaurant: UtensilsCrossed,
      library: Book,
      concert: Music,
      sportsEvent: TrendingUp,
      artGallery: Sparkles,
      bookstore: Book,
      yoga: Heart,
      grocery: ShoppingBag,
      festival: Music
    };
    return icons[venue] || MapPin;
  };

  const getVenueName = (key: string) => {
    const names: { [key: string]: string } = {
      gym: "Gym / Fitness Center",
      nightclub: "Nightclub / Dance Club",
      bar: "Bar / Lounge",
      coffeeshop: "Coffee Shop / Café",
      mall: "Shopping Mall",
      park: "Park / Outdoor Space",
      beach: "Beach / Pool",
      restaurant: "Restaurant / Dining",
      library: "Library / Study Space",
      concert: "Concert / Music Venue",
      sportsEvent: "Sports Event / Stadium",
      artGallery: "Art Gallery / Museum",
      bookstore: "Bookstore",
      yoga: "Yoga Studio / Wellness",
      grocery: "Grocery Store",
      festival: "Festival / Fair"
    };
    return names[key] || key;
  };

  const getExplanation = (venue: string, score: number) => {
    const explanations: { [key: string]: { high: string, low: string } } = {
      gym: {
        high: "Your athletic build and confident energy shine in fitness environments. Women here appreciate dedication and physical presence.",
        low: "Gym culture may not highlight your strengths. Your appeal works better in social, conversational settings."
      },
      nightclub: {
        high: "Your look thrives in nightlife - the energy, lighting, and social atmosphere enhance your features. You stand out in crowds.",
        low: "Nightclub environments may not suit your style. Your attractiveness shines more in intimate, well-lit settings."
      },
      bar: {
        high: "Casual bar settings complement your vibe. The relaxed atmosphere lets your personality and looks work together naturally.",
        low: "Bar scenes may not be your strongest venue. Consider more structured social environments."
      },
      coffeeshop: {
        high: "Your approachable, intellectual vibe works perfectly in coffee shops. Women here value conversation and authenticity.",
        low: "Coffee shop culture may not emphasize your physical strengths. Try more dynamic environments."
      },
      mall: {
        high: "Shopping environments showcase your style sense. Women notice well-dressed, confident men in retail spaces.",
        low: "Mall settings are neutral territory. Your appeal works better in activity-based or social venues."
      },
      park: {
        high: "Natural outdoor settings highlight your features well. Daylight and casual atmosphere work in your favor.",
        low: "Outdoor casual settings may not maximize your appeal. Try venues with more social structure."
      },
      beach: {
        high: "Beach/pool environments are your strongest venue! Your physique and confidence shine in casual, revealing settings.",
        low: "Beach settings may not play to your strengths. Focus on venues where personality and style matter more."
      },
      restaurant: {
        high: "Dining settings showcase your sophistication. Women appreciate well-groomed, socially adept men in restaurants.",
        low: "Restaurant environments are neutral for you. Your appeal works better in more casual or active settings."
      },
      library: {
        high: "Quiet, intellectual spaces suit your thoughtful appearance. Women here value intelligence and respectful approach.",
        low: "Library settings may not showcase your social strengths. Try more interactive environments."
      },
      concert: {
        high: "Music venues amplify your energy and style. The excitement and social atmosphere work in your favor.",
        low: "Concert chaos may not highlight your best qualities. Consider more intimate social settings."
      },
      sportsEvent: {
        high: "Sports venues are perfect for you! Your masculine energy and social confidence thrive in these settings.",
        low: "Sports environments may not suit your style. Your appeal works better in refined or artistic spaces."
      },
      artGallery: {
        high: "Cultural venues complement your sophisticated look. Women here appreciate refined taste and intellectual curiosity.",
        low: "Art gallery settings may not emphasize your strengths. Try more casual, social environments."
      },
      bookstore: {
        high: "Bookstores suit your intellectual vibe. Women here value thoughtfulness and shared interests.",
        low: "Bookstore environments may not showcase your physical presence. Try more social venues."
      },
      yoga: {
        high: "Wellness spaces highlight your balanced, health-conscious lifestyle. Women here appreciate mindfulness and fitness.",
        low: "Yoga studios may not be your strongest venue. Your appeal works better in more social settings."
      },
      grocery: {
        high: "Everyday settings like grocery stores work surprisingly well for you. Your approachable vibe attracts casual interest.",
        low: "Grocery stores are neutral territory. Focus on venues with more social interaction opportunities."
      },
      festival: {
        high: "Festival energy amplifies your social presence. The fun, casual atmosphere lets your personality shine.",
        low: "Festival chaos may not highlight your best features. Try more structured social environments."
      }
    };

    const explanation = explanations[venue];
    if (!explanation) return "Your appeal varies in this setting based on context and social dynamics.";
    
    return score >= 7.5 ? explanation.high : explanation.low;
  };

  const venues = Object.entries(venueScores).map(([key, score]) => ({
    key,
    name: getVenueName(key),
    score: score as number,
    icon: getIcon(key),
    explanation: getExplanation(key, score as number),
    trend: (score as number) >= 8.0 ? 'up' : (score as number) <= 6.0 ? 'down' : 'neutral'
  }));

  // Sort venues by score
  const sortedVenues = [...venues].sort((a, b) => b.score - a.score);
  const topVenues = sortedVenues.slice(0, 5);
  const worstVenues = sortedVenues.slice(-5).reverse();
  const freeVenues = sortedVenues.slice(0, 3);
  const lockedVenues = sortedVenues.slice(3);

  if (!isPremium) {
    return (
      <div className="space-y-6">
        {/* Free Preview */}
        <Card className="p-6 bg-white/80 backdrop-blur-sm">
          <h4 className="font-semibold text-lg mb-4 flex items-center gap-2">
            <MapPin className="h-5 w-5 text-purple-600" />
            Your Top Venues (Preview)
          </h4>
          
          <div className="space-y-4 mb-6">
            {freeVenues.map((venue, index) => {
              const Icon = venue.icon;
              return (
                <motion.div
                  key={venue.key}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-4 border-2 border-green-200"
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className="bg-green-500 p-3 rounded-xl">
                        <Icon className="h-6 w-6 text-white" />
                      </div>
                      <div>
                        <h5 className="font-bold text-gray-800">{venue.name}</h5>
                        <p className="text-xs text-gray-600">
                          {venue.trend === 'up' ? '🔥 High Appeal' : venue.trend === 'down' ? '⚠️ Low Appeal' : '✓ Moderate Appeal'}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-3xl font-bold text-green-600">{venue.score}</p>
                      <p className="text-xs text-gray-600">/ 10</p>
                    </div>
                  </div>
                  <Progress value={venue.score * 10} className="h-2 mb-3" />
                  <p className="text-sm text-gray-700 leading-relaxed">{venue.explanation}</p>
                </motion.div>
              );
            })}
          </div>

          {/* Locked Preview */}
          <div className="relative">
            <div className="absolute inset-0 bg-gradient-to-b from-transparent via-white/80 to-white z-10 flex items-end justify-center pb-8">
              <Card 
                className="p-6 bg-gradient-to-r from-purple-600 to-pink-600 text-white cursor-pointer hover:shadow-2xl transition-all"
                onClick={onUpgrade}
              >
                <div className="text-center">
                  <Lock className="h-12 w-12 mx-auto mb-3" />
                  <h4 className="text-2xl font-bold mb-2">Unlock All {venues.length} Venues</h4>
                  <p className="mb-4 opacity-90">
                    Discover your worst venues and get specific strategies for each location
                  </p>
                  <div className="bg-white/20 rounded-lg p-3 mb-4">
                    <p className="text-sm">Premium includes:</p>
                    <ul className="text-sm space-y-1 mt-2">
                      <li>✓ Complete venue rankings (best to worst)</li>
                      <li>✓ Detailed explanations for each venue</li>
                      <li>✓ Specific approach strategies</li>
                      <li>✓ Time-of-day recommendations</li>
                    </ul>
                  </div>
                  <p className="text-xl font-bold">Only $9.99 - One Time Payment</p>
                </div>
              </Card>
            </div>
            
            <div className="blur-sm opacity-50 space-y-3">
              {lockedVenues.slice(0, 3).map((venue, index) => {
                const Icon = venue.icon;
                return (
                  <div key={venue.key} className="bg-gray-100 rounded-xl p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <Icon className="h-6 w-6 text-gray-400" />
                        <span className="font-semibold text-gray-600">{venue.name}</span>
                      </div>
                      <span className="text-2xl font-bold text-gray-400">{venue.score}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Top Venues */}
      <Card className="p-8 bg-gradient-to-br from-green-50 to-emerald-50 border-2 border-green-200">
        <div className="flex items-center gap-3 mb-6">
          <TrendingUp className="h-8 w-8 text-green-600" />
          <h3 className="text-2xl font-bold text-gray-800">Your Best Venues</h3>
        </div>
        <p className="text-gray-600 mb-6">
          These locations maximize your attractiveness. Focus your efforts here for best results:
        </p>

        <div className="space-y-4">
          {topVenues.map((venue, index) => {
            const Icon = venue.icon;
            return (
              <motion.div
                key={venue.key}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-xl p-5 shadow-md hover:shadow-lg transition-all"
              >
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className="bg-gradient-to-br from-green-500 to-emerald-600 p-3 rounded-xl">
                      <Icon className="h-6 w-6 text-white" />
                    </div>
                    <div>
                      <h4 className="font-bold text-lg text-gray-800">{venue.name}</h4>
                      <p className="text-xs text-green-600 font-semibold">
                        #{index + 1} Best Venue • {venue.trend === 'up' ? '🔥 High Appeal' : '✓ Strong Appeal'}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-3xl font-bold text-green-600">{venue.score}</p>
                    <p className="text-xs text-gray-600">/ 10</p>
                  </div>
                </div>
                <Progress value={venue.score * 10} className="h-2 mb-3" />
                <p className="text-sm text-gray-700 leading-relaxed mb-3">{venue.explanation}</p>
                <div className="bg-blue-50 rounded-lg p-3">
                  <p className="text-xs text-blue-900">
                    <strong>💡 Strategy:</strong> {getStrategy(venue.key, venue.score)}
                  </p>
                </div>
              </motion.div>
            );
          })}
        </div>
      </Card>

      {/* Worst Venues */}
      <Card className="p-8 bg-gradient-to-br from-orange-50 to-red-50 border-2 border-orange-200">
        <div className="flex items-center gap-3 mb-6">
          <TrendingDown className="h-8 w-8 text-orange-600" />
          <h3 className="text-2xl font-bold text-gray-800">Your Challenging Venues</h3>
        </div>
        <p className="text-gray-600 mb-6">
          <strong>Honest analysis:</strong> These venues don't maximize your appeal. Either avoid them or use specific strategies:
        </p>

        <div className="space-y-4">
          {worstVenues.map((venue, index) => {
            const Icon = venue.icon;
            return (
              <motion.div
                key={venue.key}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-xl p-5 shadow-md border-l-4 border-orange-400"
              >
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className="bg-gradient-to-br from-orange-500 to-red-600 p-3 rounded-xl">
                      <Icon className="h-6 w-6 text-white" />
                    </div>
                    <div>
                      <h4 className="font-bold text-lg text-gray-800">{venue.name}</h4>
                      <p className="text-xs text-orange-600 font-semibold">
                        Lower Appeal • {venue.trend === 'down' ? '⚠️ Avoid' : '⚡ Needs Strategy'}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-3xl font-bold text-orange-600">{venue.score}</p>
                    <p className="text-xs text-gray-600">/ 10</p>
                  </div>
                </div>
                <Progress value={venue.score * 10} className="h-2 mb-3" />
                <p className="text-sm text-gray-700 leading-relaxed mb-3">{venue.explanation}</p>
                <div className="bg-blue-50 rounded-lg p-3">
                  <p className="text-xs text-blue-900">
                    <strong>💡 Compensation Strategy:</strong> {getCompensationStrategy(venue.key)}
                  </p>
                </div>
              </motion.div>
            );
          })}
        </div>
      </Card>

      {/* Summary Card */}
      <Card className="p-6 bg-gradient-to-br from-purple-50 to-pink-50 border-2 border-purple-200">
        <div className="flex items-start gap-3">
          <Sparkles className="h-6 w-6 text-purple-600 mt-1" />
          <div>
            <h4 className="font-bold text-lg text-gray-800 mb-3">Key Takeaways</h4>
            <ul className="space-y-2 text-sm text-gray-700">
              <li className="flex items-start gap-2">
                <span className="text-green-600 font-bold">✓</span>
                <span><strong>Focus on {topVenues[0].name}</strong> - your absolute best venue ({topVenues[0].score}/10)</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-orange-600 font-bold">⚠️</span>
                <span><strong>Avoid {worstVenues[0].name}</strong> unless you use compensation strategies ({worstVenues[0].score}/10)</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-blue-600 font-bold">💡</span>
                <span>Your appeal varies by <strong>{(topVenues[0].score - worstVenues[0].score).toFixed(1)} points</strong> depending on venue - location matters!</span>
              </li>
            </ul>
          </div>
        </div>
      </Card>
    </div>
  );
};

// Helper function for strategies
const getStrategy = (venue: string, score: number): string => {
  const strategies: { [key: string]: string } = {
    gym: "Go during peak hours (6-8pm). Wear fitted athletic wear. Make eye contact between sets. Offer to spot or ask for form advice.",
    nightclub: "Dress sharp but comfortable. Position yourself near the bar or dance floor edge. Use confident body language. Approach with energy.",
    bar: "Sit at the bar, not a table. Order confidently. Strike up conversations with bartender first. Use humor and be relaxed.",
    beach: "Arrive mid-morning when it's less crowded. Bring a frisbee or volleyball. Wear flattering swimwear. Offer sunscreen or help with umbrellas.",
    sportsEvent: "Wear team colors. Be enthusiastic but not obnoxious. Share snacks or drinks. Comment on the game to start conversations.",
    concert: "Arrive early to secure good spots. Dance confidently. Offer to buy drinks. Bond over the music.",
    restaurant: "Dress well. Be polite to staff. Show good table manners. Engage in interesting conversation.",
    coffeeshop: "Bring a book or laptop. Sit near the counter. Make friendly small talk when ordering. Be approachable.",
    park: "Bring a dog or frisbee. Jog or exercise visibly. Smile and say hi to regulars. Offer to take photos for groups.",
    mall: "Dress stylishly. Shop with purpose. Ask for opinions on items. Be friendly with staff.",
    festival: "Wear fun, colorful clothing. Participate in activities. Share food or drinks. Be social and energetic.",
    yoga: "Arrive early. Be respectful and quiet. Show good form. Chat briefly after class, not during.",
    grocery: "Shop during evening hours. Dress casually but neat. Ask for recommendations. Make light jokes about products.",
    library: "Study in common areas. Dress smart-casual. Offer to share outlets or tables. Keep interactions brief and respectful.",
    bookstore: "Browse popular sections. Ask staff for recommendations. Comment on books others are looking at. Be genuine.",
    artGallery: "Dress sophisticatedly. Take time viewing art. Make thoughtful comments. Attend opening events."
  };
  return strategies[venue] || "Be confident, approachable, and authentic. Let your personality shine.";
};

const getCompensationStrategy = (venue: string): string => {
  const strategies: { [key: string]: string } = {
    gym: "If gym isn't your strength, focus on group fitness classes where personality matters more than physique.",
    nightclub: "If nightclubs don't suit you, try lounges or rooftop bars with better lighting and conversation opportunities.",
    library: "If libraries don't work, try coffee shops or bookstores with more social atmosphere.",
    coffeeshop: "If coffee shops are weak, try restaurants or bars where your style can shine more.",
    mall: "If malls don't work, try boutique shopping districts or farmers markets with more personal interaction.",
    artGallery: "If galleries aren't your scene, try more casual cultural events like street fairs or food festivals.",
    bookstore: "If bookstores don't work, try libraries during events or book clubs with more structure.",
    grocery: "If grocery stores are weak, try farmers markets or specialty food stores with more social atmosphere.",
    bar: "If bars don't suit you, try wine tastings or brewery tours with more structured social interaction.",
    restaurant: "If restaurants are challenging, try casual dining or food trucks where atmosphere is more relaxed.",
    yoga: "If yoga studios don't work, try other fitness classes like dance or martial arts with more energy.",
    park: "If parks are weak, try organized outdoor activities like hiking groups or sports leagues.",
    beach: "If beaches don't work, try pool parties or boat events with more social structure.",
    concert: "If concerts are overwhelming, try smaller venue shows or acoustic performances.",
    sportsEvent: "If sports events don't work, try sports bars where you can watch games in a more intimate setting.",
    festival: "If festivals are too chaotic, try smaller community events or themed parties with more structure."
  };
  return strategies[venue] || "Focus on venues that play to your strengths. Don't force situations that don't suit your style.";
};

export default VenueAnalysis;